<template>
  <div>
    <div class="p-1 flex border-b border-gray-100 justify-end">
      <zoom-center-transition group class="flex flex-wrap">
        <div key="project_copy" class="relative group">
          <ranch-button
            :label="i18n.buttons.submit"
            icon="clone"
            action="action-secondary"
            v-if="canCopyProject"
          />
          <div
            class="absolute top-0 right-0 rounded bg-white border z-10 mt-8 shadow-md hidden group-hover:block"
          >
            <button
              class="p-2 w-full relative z-1 whitespace-no-wrap text-right hover:bg-menu-hover rounded-t"
              @click="submitWithSameParameters"
            >
              <span v-text="i18n.buttons.submit_same"></span>
            </button>
            <button
              class="p-2 w-full relative z-1 whitespace-no-wrap text-right hover:bg-menu-hover rounded-b"
              @click="submitWithDifferentParameters"
            >
              <span v-text="i18n.buttons.submit_diff"></span>
            </button>
          </div>
        </div>
      </zoom-center-transition>
    </div>
    <div>
      <fade-transition group>
        <list-item
          :label="i18n.cost.label"
          v-if="project.cost.raw > 0"
          key="project_cost"
        >
          <span v-html="project.cost.html"></span>
        </list-item>

        <list-item
          :label="i18n.rendering_duration.label"
          v-if="project.rendering_duration_s > 0"
          key="project_rendering_duration_s"
        >
          {{ durationStr(project.rendering_duration_s) }}
        </list-item>

        <details
          v-if="hasStatusError && statusErrorDetails"
          key="project_status_error_details"
          class="-mt-1 border-red-200 bg-red-100 border-t text-red-800 border-b text-sm"
          :open="project.status_error.id === 'overdrafted' && safeAmount < 0"
        >
          <summary class="cursor-pointer hover:bg-red-200 py-2">
            {{ statusErrorLabel }}
          </summary>
          <p
            class="p-2 m-0"
            :class="
              project.status_error.id === 'overdrafted' && safeAmount < 0
                ? 'pb-0'
                : ''
            "
            v-text="statusErrorDetails"
          ></p>
          <safe-warning
            v-if="project.status_error.id === 'overdrafted' && safeAmount < 0"
          ></safe-warning>
        </details>
        <list-item
          :label="statusErrorLabel"
          v-else-if="hasStatusError"
          class="-mt-1 border-red-200 bg-red-100 border-t text-red-800"
          key="project_status_error"
        >
          <font-awesome-icon icon="exclamation-circle" />
        </list-item>
        <div
          v-else-if="!project.frames.missing && project.cost.raw > 0"
          key="project_cost_separator"
          class="border-t border-gray-600"
        />

        <details
          v-if="project.frames.missing"
          key="project_missing_frames_details"
          class="-mt-1 border-red-200 bg-red-100 border-t text-red-800 border-b text-sm"
          :open="
            safeAmount > $store.state.account.safe.amount.min_safe_to_submit
          "
        >
          <summary class="cursor-pointer hover:bg-red-200 py-2">
            {{ i18n.warnings.missing_frames }}
            <span class="float-right open:hidden">{{ missingFrames }}</span>
          </summary>
          <div class="flex justify-center pt-5">
            <ranch-button
              :label="
                i18n.buttons.resubmit + ' ' + i18n.warnings.missing_frames
              "
              icon="redo-alt"
              @click="resubmitMissingFrames"
              :action="
                safeAmount > $store.state.account.safe.amount.min_safe_to_submit
                  ? 'action'
                  : 'action-secondary'
              "
            ></ranch-button>
          </div>
          <p class="p-5 m-0 break-words">
            {{ project.frames.missing }}
          </p>
        </details>

        <list-item
          :label="i18n.params.max_budget"
          v-if="project.max_budget > 0"
          key="project_max_budget"
        >
          <div>
            <ranch-credit
              class="block text-right"
              :amount="project.max_budget"
            />
            <details ref="increaseBudgetDetails" v-if="mayIncreaseBudget">
              <summary
                class="text-right cursor-pointer"
                v-text="i18n.buttons.increase_budget"
              />
              <form @submit.prevent="increaseBudget" class="text-right">
                <label class="flex text-base items-center">
                  <span
                    class="flex-auto whitespace-no-wrap mr-2"
                    v-text="i18n.buttons.new_budget"
                  />
                  <input
                    type="number"
                    class="text-right w-16"
                    v-model="new_budget"
                    :min="1 + +project.max_budget"
                    required
                  /><span class="ranch-credit ml-1">€</span>
                </label>
                <div class="flex justify-end">
                  <ranch-button
                    :label="i18n.buttons.increase_budget"
                    icon="money-bill-alt"
                    action="action-secondary mr-0"
                    type="submit"
                  />
                </div>
              </form>
            </details>
          </div>
        </list-item>

        <list-item
          :label="i18n.params.priority + ' ' + project.priority.name"
          key="project_priority"
          v-if="project.priority.id > 0"
        >
          <div class="flex text-right">
            <span v-html="project.priority.price"></span>
          </div>
        </list-item>

        <list-item
          :label="i18n.application"
          v-if="project.application !== ''"
          key="project_application"
        >
          <span>{{ project.application.label }}</span>
          <img
            :src="project.application.icon"
            alt="Application"
            width="auto"
            height="auto"
            class="w-5 h-5 ml-2 -mt-1"
            v-if="project.application.icon !== null"
          />
        </list-item>

        <list-item
          :label="i18n.renderer"
          v-if="project.application.renderer.label !== null"
          key="project_renderer"
        >
          <span>{{ project.application.renderer.label }}</span>
          <img
            :src="project.application.renderer.icon"
            alt="Renderer"
            width="auto"
            height="auto"
            class="w-5 h-5 ml-2 -mt-1"
          />
        </list-item>

        <list-item :label="i18n.params.resolution" key="project_resolution">
          <span
            v-if="
              project.params.resolution.x > 0 && project.params.resolution.y > 0
            "
          >
            {{ project.params.resolution.x }} x
            {{ project.params.resolution.y }}
          </span>
          <span v-else>{{ i18n.params.from_software }}</span>
        </list-item>

        <list-item
          :label="i18n.params.frames"
          key="project_frames"
          class="overflow-x-auto whitespace-no-wrap"
        >
          <span
            v-if="
              project.params.frames.start > 0 || project.params.frames.end > 0
            "
            class="pl-3"
          >
            {{ project.params.frames.start }} →
            {{ project.params.frames.end }}
          </span>
          <span v-else class="pl-3">{{ i18n.params.from_software }}</span>

          <div v-if="project.frames.submitted != ''" class="pl-3">
            ({{ project.frames.submitted }})
          </div>
        </list-item>

        <list-item
          :label="i18n.params.frames_number"
          v-if="project.frames.count > 0"
          key="project_frames_count"
        >
          {{ project.frames.count }}
        </list-item>

        <list-item
          :label="i18n.params.camera"
          v-if="project.camera !== null && project.camera !== ''"
          key="project_camera"
        >
          {{ project.camera }}
        </list-item>
        <list-item
          :label="i18n.params.spp"
          v-if="project.spp > 0 && project.application.slug === 'indigo'"
          key="project_spp"
        >
          {{ project.spp }}
        </list-item>

        <list-item
          :label="i18n.params.sl"
          v-if="project.sl > 0 && project.application.slug === 'maxwell'"
          key="project_sl"
        >
          {{ project.sl }}
        </list-item>

        <list-item
          :label="i18n.params.take"
          v-if="project.customization.take"
          key="project_take"
        >
          {{ project.customization.take }}
        </list-item>

        <list-item
          :label="i18n.params.layer"
          v-if="project.customization.layer"
          key="project_layer"
        >
          {{ project.customization.layer }}
        </list-item>

        <dates
          class="border-t border-gray-600"
          :i18n="i18n.step"
          :dates="project.dates"
          key="project_history"
        ></dates>
      </fade-transition>
    </div>
  </div>
</template>

<script>
import ListItem from "../Design/ListItem";
import Dates from "./_Dates";

import { FadeTransition, ZoomCenterTransition } from "vue2-transitions";
import axios from "axios";
import { handleAxiosError } from "@/boot/functions";
import { computed, reactive, ref, toRefs, watch } from "@vue/composition-api";
import { useStore } from "@/store/composition";

import NewProjectModal from "../Projects/NewProject/Modal";
import NewProjectModalConfig from "../Modals/configs/newproject";

export default {
  props: ["project", "i18n"],
  components: {
    ListItem,
    FadeTransition,
    ZoomCenterTransition,
    Dates,
  },
  setup(props, context) {
    return {
      ...setupBudget(props, context),
    };
  },
  methods: {
    submitWithDifferentParameters() {
      let missingFrames = this.project.frames.submitted;
      const frameRange = this.project.params.frames;
      if (!missingFrames && (frameRange.start > 0 || frameRange.end > 0)) {
        missingFrames = frameRange.start + "-" + frameRange.end;
      }
      this.$modal.show(
        NewProjectModal,
        {
          copyMode: true,
          projectToCopy: this.project,
          step: 4,
          missingFrames,
        },
        NewProjectModalConfig
      );
    },
    resubmitMissingFrames() {
      this.$modal.show(
        NewProjectModal,
        {
          copyMode: true,
          projectToCopy: this.project,
          step: 4,
          missingFrames: this.project.frames.missing,
        },
        NewProjectModalConfig
      );
    },
    submitWithSameParameters() {
      let endpoint = this.$store.state.dashboard.projects.routes.copy.replace(
        ":id",
        this.project.id
      );
      axios
        .post(endpoint)
        .then(({ data }) => {
          let project = data.data;

          this.$store.dispatch("dashboard/getPaginator", {
            endpoint: this.$store.state.dashboard.projects.routes.index,
            locale: this.locale,
          });

          this.$router.push({
            name: "project.priority",
            params: {
              id: project.id,
              type: this.$route.params.type,
              locale: this.$route.params.locale,
            },
          });
        })
        .catch(handleAxiosError);
    },
    durationStr(seconds) {
      const seconds_per_hour = 3600;
      const seconds_per_minute = 60;
      const h = Math.floor(seconds / seconds_per_hour);
      if (h > 0) {
        let txt = h + "h";
        const m = Math.floor(
          (seconds - h * seconds_per_hour) / seconds_per_minute
        );
        if (m) {
          txt += " " + m + "m";
        }
        return txt;
      }

      const m = Math.floor(seconds / seconds_per_minute);
      const s = seconds - seconds_per_minute * m;
      if (m > 0) {
        let txt = m + "m";
        if (s) {
          txt += " " + s + "s";
        }
        return txt;
      }
      return seconds + "s";
    },
  },
  computed: {
    canCopyProject() {
      return this.project.version > 1;
    },

    statusErrorLabel() {
      return this.project.status_error.label !== ""
        ? this.project.status_error.label
        : this.project.status_error.id;
    },

    hasStatusError() {
      return this.project.status_error.id !== "";
    },
    statusErrorDetails() {
      return this.project.status_error.details;
    },

    safeAmount() {
      if (this.$store.state.account.safe !== undefined) {
        return this.$store.state.account.safe.amount.raw;
      }

      return 0;
    },

    missingFrames() {
      let frames = this.project.frames.missing;
      return frames !== "" && frames.length < 15
        ? frames
        : frames.slice(0, 12) + "...";
    },
  },
};

function setupBudget(props, { emit }) {
  const increaseBudgetDetails = ref(null);
  const data = reactive({
    new_budget: props.project.max_budget,
    increasingBudget: false,
  });

  watch(
    () => props.project.max_budget,
    () => {
      data.new_budget = props.project.max_budget;
    }
  );

  const store = useStore();
  const increaseBudget = () => {
    if (data.increasingBudget) {
      return;
    }
    axios
      .put(
        store.state.dashboard.projects.routes.budget.replace(
          ":id",
          props.project.id
        ),
        { budget: data.new_budget }
      )
      .then(() => {
        emit("reload");
      })
      .catch(handleAxiosError)
      .then(() => {
        data.increasingBudget = false;
        increaseBudgetDetails.value.open = false;
      });
  };

  const mayIncreaseBudget = computed(() => {
    return !["finished", "invalid", "discarded", "removed"].includes(
      props.project.status
    );
  });
  return {
    ...toRefs(data),
    increaseBudget,
    increaseBudgetDetails,
    mayIncreaseBudget,
  };
}
</script>

<style>
details[open] .open\:hidden {
  display: none;
}
</style>
